import React, { useCallback } from 'react';
import { Option, Select } from 'carbon-react/lib/components/select';

import { useIntl } from '@futrli/localisation';
import { ListingPageFilterBar } from '@futrli/ui-components/components/listing-page/listing-page-filter-bar/listing-page-filter-bar';

import { SortOption } from '../../../../utils/list-filters/list-filter-utils';
import { SchedulingStatusFilter } from './hooks/use-report-scheduling-filter';

interface ReportSchedulingFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: SchedulingStatusFilter;
  onStatusChange: (value: SchedulingStatusFilter) => void;
  sortValue: string;
  onSortChange: (value: string) => void;
  sortOptions: SortOption[];
  stickyTop?: number;
}

/**
 * Filter header for the Report Scheduling tab.
 * Renders Search + Status dropdown (All/Scheduled/Awaiting review/Expired) + Sort by.
 * The Status dropdown uses a Carbon Select (not a ButtonToggle) as per the spec.
 */
export const ReportSchedulingFilters = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  sortValue,
  onSortChange,
  sortOptions,
  stickyTop
}: ReportSchedulingFiltersProps) => {
  const intl = useIntl();

  const handleStatusChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onStatusChange(event.target.value as SchedulingStatusFilter);
    },
    [onStatusChange]
  );

  const handleSortChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSortChange(event.target.value);
    },
    [onSortChange]
  );

  return (
    <ListingPageFilterBar
      searchTerm={searchTerm}
      onSearchChange={onSearchChange}
      stickyTop={stickyTop}
      rightContent={
        <div style={{ minWidth: 250 }}>
          <Select
            id="scheduling-sort-by"
            value={sortValue}
            onChange={handleSortChange}
            size="medium"
            label={intl.formatMessage({ id: 'predict.sort_by', defaultMessage: 'Sort by' })}
            aria-label={intl.formatMessage({ id: 'predict.sort_by', defaultMessage: 'Sort by' })}
          >
            {sortOptions.map(opt => (
              <Option key={opt.value} value={opt.value} text={opt.label} />
            ))}
          </Select>
        </div>
      }
    >
      <div style={{ minWidth: 200 }}>
        <Select
          id="scheduling-status"
          value={statusFilter}
          onChange={handleStatusChange}
          size="medium"
          label={intl.formatMessage({ id: 'common.status', defaultMessage: 'Status' })}
          aria-label={intl.formatMessage({ id: 'common.status', defaultMessage: 'Status' })}
        >
          <Option value="all" text={intl.formatMessage({ id: 'predict.all', defaultMessage: 'All' })} />
          <Option
            value="scheduled"
            text={intl.formatMessage({ id: 'predict.scheduled', defaultMessage: 'Scheduled' })}
          />
          <Option
            value="awaiting-review"
            text={intl.formatMessage({ id: 'reporting.awaiting_review', defaultMessage: 'Awaiting review' })}
          />
          <Option value="expired" text={intl.formatMessage({ id: 'common.expired', defaultMessage: 'Expired' })} />
        </Select>
      </div>
    </ListingPageFilterBar>
  );
};
